import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, SectionLabel } from "@/components/Primitives";
import { Loader2, Plus, Trash2, Building2, TrendingUp, ArrowRight, Trophy } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

function fmt(n) { return (n ?? 0).toLocaleString("en-US"); }
function fmtMoney(n) {
  if (n == null) return "—";
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export default function Advertisers() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [scorecards, setScorecards] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", industry: "", contact_email: "", notes: "" });
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    const [{ data }, { data: cards }] = await Promise.all([
      api.get("/advertisers/"),
      api.get("/advertisers/scorecards"),
    ]);
    setItems(data);
    setScorecards(cards);
  };

  useEffect(() => {
    (async () => { try { await load(); } finally { setLoading(false); } })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError("");
    try {
      await api.post("/advertisers/", {
        name: form.name.trim(),
        industry: form.industry.trim() || null,
        contact_email: form.contact_email.trim() || null,
        notes: form.notes.trim() || null,
      });
      await load();
      setForm({ name: "", industry: "", contact_email: "", notes: "" });
      toast.success("Advertiser added");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  const remove = async (id, name) => {
    if (!confirm(`Delete advertiser "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/advertisers/${id}`);
      await load();
      toast.success("Advertiser deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  // top-of-page aggregate
  const totals = Object.values(scorecards).reduce((acc, s) => {
    acc.campaigns += s.total_campaigns || 0;
    acc.spend += s.spend_usd || 0;
    acc.impressions += s.impressions || 0;
    acc.conversions += s.conversions || 0;
    return acc;
  }, { campaigns: 0, spend: 0, impressions: 0, conversions: 0 });

  return (
    <div className="px-8 md:px-12 py-10 max-w-[1500px]">
      <PageHeader
        eyebrow="Module / 08"
        title="Advertisers"
        description="Roster of advertisers plus a live scorecard per brand — campaigns, spend, impressions, conversions, and best-performing campaign."
        testid="advertisers-header"
      />

      {/* Aggregate */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <div className="border border-[#E0E0D8] bg-white p-5">
          <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-[#555]">Advertisers</p>
          <p className="font-mono text-3xl tracking-tighter mt-2" data-testid="agg-advertisers">{items.length}</p>
        </div>
        <div className="border border-[#E0E0D8] bg-white p-5">
          <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-[#555]">Campaigns</p>
          <p className="font-mono text-3xl tracking-tighter mt-2">{fmt(totals.campaigns)}</p>
        </div>
        <div className="border border-[#E0E0D8] bg-white p-5">
          <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-[#555]">Total spend</p>
          <p className="font-mono text-3xl tracking-tighter mt-2">{fmtMoney(totals.spend)}</p>
        </div>
        <div className="border border-[#E0E0D8] bg-white p-5">
          <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-[#555]">Conversions</p>
          <p className="font-mono text-3xl tracking-tighter mt-2 text-[#FF3E00]">{fmt(totals.conversions)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <form onSubmit={submit} data-testid="add-advertiser-form" className="lg:col-span-4 bg-white border border-[#E0E0D8] p-6 h-fit">
          <SectionLabel>New advertiser</SectionLabel>
          <div className="space-y-4">
            <div>
              <label className="text-[11px] font-mono uppercase tracking-[0.15em] text-[#555]">Name *</label>
              <input
                data-testid="advertiser-name-input"
                required value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="mt-1 w-full bg-white border border-[#E0E0D8] rounded-none px-3 py-2.5 font-mono text-sm focus:border-[#FF3E00] focus:outline-none"
              />
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-[0.15em] text-[#555]">Industry</label>
              <input
                data-testid="advertiser-industry-input"
                value={form.industry}
                onChange={(e) => setForm((f) => ({ ...f, industry: e.target.value }))}
                placeholder="e.g. Retail, Finance"
                className="mt-1 w-full bg-white border border-[#E0E0D8] rounded-none px-3 py-2.5 font-mono text-sm focus:border-[#FF3E00] focus:outline-none"
              />
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-[0.15em] text-[#555]">Contact email</label>
              <input
                data-testid="advertiser-email-input"
                type="email" value={form.contact_email}
                onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
                className="mt-1 w-full bg-white border border-[#E0E0D8] rounded-none px-3 py-2.5 font-mono text-sm focus:border-[#FF3E00] focus:outline-none"
              />
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-[0.15em] text-[#555]">Notes</label>
              <textarea
                data-testid="advertiser-notes-input"
                rows={3} value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                className="mt-1 w-full bg-white border border-[#E0E0D8] rounded-none px-3 py-2.5 font-mono text-sm focus:border-[#FF3E00] focus:outline-none"
              />
            </div>
            {error && <p data-testid="advertiser-error" className="text-xs font-mono text-[#D91919]">{error}</p>}
            <button
              type="submit" disabled={submitting}
              data-testid="advertiser-submit-button"
              className="w-full bg-[#FF3E00] text-white py-3 font-semibold hover:bg-[#D93500] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {submitting ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
              {submitting ? "Adding…" : "Add advertiser"}
            </button>
          </div>
        </form>

        <div className="lg:col-span-8">
          <SectionLabel>Scorecard ({items.length})</SectionLabel>
          {loading ? (
            <p className="text-sm text-[#555] flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</p>
          ) : items.length === 0 ? (
            <div className="border border-dashed border-[#E0E0D8] bg-white p-12 text-center">
              <Building2 size={28} className="mx-auto text-[#888]" strokeWidth={1.25} />
              <p className="mt-3 text-xs font-mono uppercase tracking-[0.2em] text-[#555]">No advertisers yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((a) => {
                const sc = scorecards[a.id] || {};
                const open = expanded === a.id;
                return (
                  <div key={a.id} data-testid={`advertiser-row-${a.id}`} className="border border-[#E0E0D8] bg-white">
                    <button
                      onClick={() => setExpanded(open ? null : a.id)}
                      data-testid={`advertiser-expand-${a.id}`}
                      className="w-full text-left px-5 py-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-center hover:bg-[#F4F4F0] transition-colors"
                    >
                      <div className="md:col-span-4">
                        <p className="text-sm font-semibold leading-tight">{a.name}</p>
                        <p className="text-[10px] font-mono uppercase tracking-wider text-[#555] mt-1">
                          {a.industry || "—"} {a.contact_email && `· ${a.contact_email}`}
                        </p>
                      </div>
                      <div className="md:col-span-2 text-right">
                        <p className="text-[10px] font-mono uppercase tracking-wider text-[#555]">Campaigns</p>
                        <p className="font-mono text-lg">{fmt(sc.total_campaigns)}</p>
                      </div>
                      <div className="md:col-span-2 text-right">
                        <p className="text-[10px] font-mono uppercase tracking-wider text-[#555]">Spend</p>
                        <p className="font-mono text-lg">{fmtMoney(sc.spend_usd)}</p>
                      </div>
                      <div className="md:col-span-2 text-right">
                        <p className="text-[10px] font-mono uppercase tracking-wider text-[#555]">Conv.</p>
                        <p className="font-mono text-lg text-[#FF3E00]">{fmt(sc.conversions)}</p>
                      </div>
                      <div className="md:col-span-2 flex items-center justify-end gap-2">
                        {isAdmin && (
                          <button
                            onClick={(e) => { e.stopPropagation(); remove(a.id, a.name); }}
                            data-testid={`advertiser-delete-${a.id}`}
                            className="text-[#D91919] hover:bg-[#D91919] hover:text-white border border-[#D91919]/30 px-2 py-1 transition-colors"
                            title="Delete advertiser"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                        <ArrowRight size={14} className={`transition-transform text-[#888] ${open ? "rotate-90" : ""}`} />
                      </div>
                    </button>

                    {open && (
                      <div className="border-t border-[#E0E0D8] bg-[#F4F4F0]/40 px-5 py-5" data-testid={`advertiser-scorecard-${a.id}`}>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-5">
                          <Metric label="Total budget" value={fmtMoney(sc.total_budget_usd)} />
                          <Metric label="Impressions" value={fmt(sc.impressions)} />
                          <Metric label="Clicks" value={fmt(sc.clicks)} sub={`CTR ${sc.ctr ?? 0}%`} />
                          <Metric label="Conversions" value={fmt(sc.conversions)} />
                          <Metric label="Accepted" value={`${sc.accepted_campaigns ?? 0} / ${sc.total_campaigns ?? 0}`} />
                        </div>

                        {sc.best_campaign ? (
                          <div className="border border-[#0A0A0A] bg-white p-4 flex flex-col md:flex-row gap-3 md:items-center justify-between">
                            <div className="flex items-start gap-3">
                              <Trophy size={20} className="text-[#FF3E00] mt-0.5" />
                              <div>
                                <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#FF3E00]">Best-performing campaign</p>
                                <p className="text-sm font-semibold mt-0.5">{sc.best_campaign.name}</p>
                                <p className="text-[11px] font-mono text-[#555] mt-0.5">
                                  {fmt(sc.best_campaign.conversions)} conversions · {fmt(sc.best_campaign.impressions)} impressions · {fmtMoney(sc.best_campaign.spend_usd)} spend
                                </p>
                              </div>
                            </div>
                            <Link
                              to={`/campaigns/${sc.best_campaign.id}`}
                              data-testid={`advertiser-best-link-${a.id}`}
                              className="text-[11px] font-mono uppercase tracking-wider text-[#FF3E00] inline-flex items-center gap-1 hover:underline shrink-0"
                            >
                              Open campaign <ArrowRight size={11} />
                            </Link>
                          </div>
                        ) : (
                          <p className="text-xs font-mono text-[#555] flex items-center gap-2">
                            <TrendingUp size={14} /> No campaign performance data yet — push & sync a campaign to populate the scorecard.
                          </p>
                        )}

                        <div className="mt-4 flex gap-2">
                          <Link
                            to={`/campaigns?advertiser=${a.id}`}
                            onClick={() => {/* CampaignsList reads its own filter; this is informational */}}
                            className="text-[11px] font-mono uppercase tracking-wider border border-[#0A0A0A] px-3 py-1.5 hover:bg-[#0A0A0A] hover:text-white transition-colors"
                          >
                            View all campaigns
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, sub }) {
  return (
    <div className="border border-[#E0E0D8] bg-white p-3">
      <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#555]">{label}</p>
      <p className="font-mono text-xl tracking-tighter mt-1">{value}</p>
      {sub && <p className="text-[10px] font-mono text-[#888] mt-0.5">{sub}</p>}
    </div>
  );
}
